import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { busType } from "../../../../helpers";
import BookingPolicies from "../../BookingPolicies";
import BusPhoto from "../../BusPhoto/BusPhoto.jsx";
import BusSeat from "../../BusSeat/index.jsx";
import Reviews from "../../Reviews/";
import {
  CardWrapper,
  SingleCard,
  MainRow,
  OperatorSection,
  TimelineSection,
  TimeNode,
  JourneyLine,
  BookingSection,
  BookingBtn,
  TabLinksWrapper,
  TabLink,
} from "./BusName.styles.js";

// Custom SVG Icons for details
const StarIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
  </svg>
);

const TimelineBusIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M4 16c0 .88.39 1.67 1 2.22V20c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h8v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1.78c.61-.55 1-1.34 1-2.22V6c0-3.5-3.58-4-8-4s-8 .5-8 4v10zm3.5 1c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm9 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM18 10H6V6h12v4z"/>
  </svg>
);

const PhotoIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
  </svg>
);

const ReviewIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8 12l-3.76 2.27 1-4.28-3.32-2.88 4.38-.38L12 5l1.7 4.73 4.38.38-3.32 2.88 1 4.28L12 14z"/>
  </svg>
);

const PolicyIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
  </svg>
);

const SeatIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M4 18v3h3v-3h10v3h3v-3h1c1.1 0 2-.9 2-2V9c0-1.1-.9-2-2-2h-1V5c0-1.1-.9-2-2-2H7c-1.1 0-2 .9-2 2v2H4c-1.1 0-2 .9-2 2v7c0 1.1.9 2 2 2h1zm3-13h10v2H7V5z"/>
  </svg>
);

const BusName = ({ tripData, fleet, setRoundCheck, setFirstJourneyDateCheck }) => {
  const { webSettingData, languageData } = useSelector(
    (state) => state.busLists
  );

  const [location, setLocation] = useState([]);
  const [singleBusPhoto, setSinglePhoto] = useState([]);
  const [showBusPhoto, setShowBusPhoto] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [showBookingPolicies, setShowBookingPolicies] = useState(false);
  const [showBusSeat, setShowBusSeat] = useState(false);

  const pickLocation = location.find(
    (item) => item?.id === tripData?.pick_location_id
  );

  const dropLocation = location.find(
    (item) => item?.id === tripData?.drop_location_id
  );

  useEffect(() => {
    const tenantQuery = tripData?.tenant_subdomain ? `?tenant_subdomain=${tripData.tenant_subdomain}` : '';
    fetch(
      `${process.env.REACT_APP_API_MODULE_DOMAIN}/vehicles/${tripData.vehicle_id}${tenantQuery}`
    )
      .then((res) => res.json())
      .then((data) => setSinglePhoto(data.data))
      .catch((err) => console.error('Vehicle fetch error:', err));
  }, []);

  useEffect(() => {
    const tenantQuery = tripData?.tenant_subdomain ? `?tenant_subdomain=${tripData.tenant_subdomain}` : '';
    fetch(`${process.env.REACT_APP_API_MODULE_DOMAIN}/locations${tenantQuery}`)
      .then((res) => res.json())
      .then((data) => setLocation(data.data))
      .catch((err) => console.error('Locations fetch error:', err));
  }, []);

  const showPhoto = () => {
    setShowBusPhoto((prevState) => !prevState);
    setShowReview(false);
    setShowBookingPolicies(false);
    setShowBusSeat(false);
  };

  const handleShowReview = () => {
    setShowReview((prevState) => !prevState);
    setShowBusPhoto(false);
    setShowBookingPolicies(false);
    setShowBusSeat(false);
  };

  const handleShowBookingPolicies = () => {
    setShowBookingPolicies((prevState) => !prevState);
    setShowReview(false);
    setShowBusPhoto(false);
    setShowBusSeat(false);
  };

  const [bookings, setBookings] = useState([]);

  useEffect(() => {
    const storedBookings = localStorage.getItem("bookingInfo");
    if (storedBookings) {
      setBookings(JSON.parse(storedBookings));
    }
  }, []);

  const showSeat = () => {
    setRoundCheck(true);
    setShowBusSeat((prevState) => !prevState);
    setShowBookingPolicies(false);
    setShowReview(false);
    setShowBusPhoto(false);

    const bookingInfo = {
      trip_id: tripData.trip_id,
      subtripId: tripData.subtripId,
      vehicle_id: tripData.vehicle_id,
      cseat: String(tripData.child_seat),
      spseat: String(tripData.special_seat),
      tenant_subdomain: tripData.tenant_subdomain,
    };

    setBookings((prevBookings) => {
      const updatedBookings = bookingInfo;
      localStorage.setItem("bookingInfo", JSON.stringify(updatedBookings));
      return updatedBookings;
    });
  };

  const activeColor = webSettingData?.buttoncolor || "#1FBA30";
  const hoverColor = webSettingData?.buttoncolorhover || "#1aa329";
  const textColor = webSettingData?.buttontextcolor || "white";

  return (
    <CardWrapper>
      <SingleCard activeColor={activeColor}>
        <MainRow>
          {/* Operator Details */}
          <OperatorSection>
            <h4>
              {tripData?.company}
              <span className="coach-number">{tripData?.company_name}</span>
            </h4>
            <span className="bus-type">
              {busType(fleet, tripData?.fleet_id)}
            </span>
            <div className="rating-badge">
              <StarIcon />
              <span>{tripData?.rating || "4.5"}</span>
            </div>
          </OperatorSection>

          {/* Timeline and stations */}
          <TimelineSection>
            <TimeNode align="left">
              <span className="time">{tripData?.start_time}</span>
              <span className="station">{pickLocation?.name || "Loading..."}</span>
            </TimeNode>

            <JourneyLine>
              <span className="duration">{tripData?.journey_hour} Hrs</span>
              <div className="line-container">
                <div className="line" />
                <div className="bus-icon">
                  <TimelineBusIcon />
                </div>
              </div>
            </JourneyLine>

            <TimeNode align="right">
              <span className="time">{tripData?.end_time}</span>
              <span className="station">{dropLocation?.name || "Loading..."}</span>
            </TimeNode>
          </TimelineSection>

          {/* Price, Availability, Booking Button */}
          <BookingSection priceColor={activeColor}>
            <span className="price">
              ৳ {tripData.adult_fair}
            </span>
            <span className="seats-left">
              <span>{tripData?.available_seat}</span> seats left
            </span>
            <BookingBtn
              onClick={showSeat}
              btnbgcolor={activeColor}
              btnbghvcolor={hoverColor}
              btntextcolor={textColor}
            >
              {languageData?.booking_page_card_view_seats_btn
                ? languageData?.booking_page_card_view_seats_btn[webSettingData?.language]
                : "View Seats"}
            </BookingBtn>
          </BookingSection>
        </MainRow>

        {/* Action detail links */}
        <TabLinksWrapper>
          <TabLink
            onClick={showPhoto}
            active={showBusPhoto}
            activeColor={activeColor}
          >
            <PhotoIcon /> {languageData?.booking_page_card_bus_photos_btn
              ? languageData?.booking_page_card_bus_photos_btn[webSettingData?.language]
              : "Photos"}
          </TabLink>
          <TabLink
            onClick={handleShowReview}
            active={showReview}
            activeColor={activeColor}
          >
            <ReviewIcon /> {languageData?.booking_page_card_reviews_btn
              ? languageData?.booking_page_card_reviews_btn[webSettingData?.language]
              : "Reviews"}
          </TabLink>
          <TabLink
            onClick={handleShowBookingPolicies}
            active={showBookingPolicies}
            activeColor={activeColor}
          >
            <PolicyIcon /> {languageData?.booking_page_card_booking_policies_btn
              ? languageData?.booking_page_card_booking_policies_btn[webSettingData?.language]
              : "Policies"}
          </TabLink>
          <TabLink
            onClick={showSeat}
            active={showBusSeat}
            activeColor={activeColor}
          >
            <SeatIcon /> {languageData?.booking_page_card_view_seats_btn
              ? languageData?.booking_page_card_view_seats_btn[webSettingData?.language]
              : "Seat Map"}
          </TabLink>
        </TabLinksWrapper>
      </SingleCard>

      {showBusPhoto && <BusPhoto singleBusPhoto={singleBusPhoto.allimg} />}
      {showReview && (
        <Reviews id={tripData?.subtripId} webSettingData={webSettingData} />
      )}
      {showBookingPolicies && <BookingPolicies />}
      {showBusSeat && <BusSeat setFirstJourneyDateCheck={setFirstJourneyDateCheck} tripData={tripData} />}
    </CardWrapper>
  );
};

export default BusName;
